// defines model for scorecard nodes and their notes

import $ from 'jquery'

import _ from 'lodash'
import Backbone from 'backbone'
import globals from 'scoreboard/components/globals'
import permissions from 'scoreboard/components/permissions'
import Dataset from 'scoreboard/datasets/model/dataset'
import fieldAggregationDescription from 'scoreboard/datasets/field-aggregation-description'
import unwrapModel from 'scoreboard/components/unwrap-model'
import config from 'scoreboard/components/config'

var ScorecardNode = Backbone.Model.extend({

	section: 'scorecards',
	domainType: 'scorecard-item',

	// #22381 need to keep this around even though we defined an explicit
	// url function
	urlRoot: 'api/scorecards',

	url: function() {
		var url = `api/scorecards`
		if (this.id) {
			url += '/' + this.id
		}
		// #6435 need to send calendar period so that we know which notes can be
		// modified
		if (this.periodId) {
			url += '?calendarPeriodId=' + globals.periodId
		}
		return url
	},

	isEditable: function() {
		// #13029 be sure to send org ID for the bookmarks section
		var orgId = this.get('organizationId')
		return (permissions.structure(orgId) || permissions.ownersUpdaters(orgId)) &&
			(permissions.isEditableIfReplicated(this) || this.get('originallyDocked'))
	},

	/**
	 * If the scorecard overview is editable (the overview chart, for example)
	 */
	overviewEditable: function() {
		let orgId = this.get('organizationId')
		return (permissions.scorecardOverview(orgId)) &&
			(permissions.isEditableIfReplicated(this) || this.get('originallyDocked'))
	},

	initialize: function() {
		// rebuild hashes
		this.on('change:scores', function() {
			delete this._scoresById
		}.bind(this))
	},

	// retrieves a score based on the given periodId
	score: function(periodId) {
		if (!this._scoresById) {
			this._scoresById = _.transform(this.get('scores'), function(result, score) {
				result[score.periodId] = score
			})
		}
		return this._scoresById[periodId]
	},

	setScore: function(score) {
		var index = _.findIndex(this.get('scores'), function(s) {
			return s.periodId == score.periodId
		})
		if (index == -1) {
			if (!this.get('scores')) {
				this.set('scores', [])
			}
			this.get('scores').push(score)
		}
		else {
			this.get('scores')[index] = score
		}

		if (this._scoresById) {
			this._scoresById[score.periodId] = score
		}
	},

	setScores: function(scores, relatedItems) {
		_.each(scores, this.setScore.bind(this))
		this.set('relatedItems', relatedItems)
		this.trigger('change:values')
	},

	loadWeights: function(success) {
		$.get(_.get(this, 'urlRoot') + '/' + this.id + `/weights?calendarPeriodId=${globals.periodId}`, success)
	},

	saveWeights: function(weights, success) {
		weights = _.map(weights, function(weight) {
			return _.pick(weight, 'id', 'weight', 'periods')
		})
		Backbone.sync('update', this, {
			attrs: weights,
			url: _.get(this, 'urlRoot') + '/weights',
			success: success
		})
	},

	saveChart: function(chart, success, error) {
		this.set('chart', chart)
		return Backbone.sync('update', this, {
			attrs: chart,
			url: this.url() + '/chart?calendarPeriodId=' + globals.periodId,
			success: success,
			error: error
		})
	},

	untransformedDocking () {
		return this.replicated() && this.get('type') === 'docking' && this.get('originallyDocked')
	},

	transformedDocking () {
		return this.replicated() && this.get('type') !== 'docking' && this.get('originallyDocked')
	},

	replicated () {
		return !_.isNil(this.get('syncId'))
	},

	getDatasetSeriesLabel(series) {
		if (series.updateType === 'dataset') {
			const datasets = this.get('datasets')
			// if we didn't get any datasets back (possibly from insufficient permissions),
			if (!datasets) {
				return series.equationLabel
			}
			const currentSeries = JSON.parse(series.equation)
			const datasetsById = _.keyBy(datasets.datasets, 'id')
			const dataset = new Dataset(datasetsById[currentSeries.id])
			const seriesField = dataset.getField(currentSeries.columnFieldId)
			/**
			 * 	#47638 this is not series.aggregationFieldId because in a DatasetSeries
			 * 	that only represents the dataset's total display field, not the field
			 * 	we are actually aggregating see {@link DatasetSeries.kt#aggregationFieldId}
			 */
			const aggregationField = dataset.getUnderlyingField(seriesField)
			/** {@type String} */ // IntelliJ shows a JSDoc type warning for descriptionOpts without this
			const aggregationType = currentSeries.columnAggregationType
			const descriptionOpts = {
				dataset: dataset,
				aggregationField,
				aggregationVisibleLinkedField: dataset.visibleLinkedFieldsById[currentSeries.columnVisibleLinkedFieldId],
				calendarField: dataset.getUnderlyingField(currentSeries.calendarFieldId),
				aggregationType,
				tracked: dataset.get('tracked')
			}
			return fieldAggregationDescription(descriptionOpts)
		}
	},

	getDatasetSeriesLabelForEquation(equation) {
		return this.getDatasetSeriesLabel({
			updateType: 'dataset',
			equation: JSON.stringify(equation)
		})
	},

	scoringType: function () {
		return config.scoringType(this.get('scoringTypeId'))
	}

}, {
	metricish
})

function metricish(sn) {
	sn = unwrapModel(sn)
	return sn.type === 'metric' || sn.sourceType === 'metric'
}

export default ScorecardNode
export { metricish }

ScorecardNode.__filename__ = 'scorecards/scorecard-node.js'
